import { useState, useRef, type KeyboardEvent } from 'react'
import { format } from 'date-fns'
import { Trash2 } from 'lucide-react'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { useAddNote, useDeleteNote } from '@/hooks/useNotes'
import type { Note } from '@/types'

interface NoteThreadProps {
  notes: Note[] | undefined
  entity: { priorityId: string } | { bucketId: string }
  placeholder?: string
}

export function NoteThread({ notes, entity, placeholder = 'Add a note…' }: NoteThreadProps) {
  const [draft, setDraft] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const addNote = useAddNote()

  async function handleSubmit() {
    const trimmed = draft.trim()
    if (!trimmed) return
    setSubmitting(true)
    await addNote.mutateAsync({ body: trimmed, entity })
    setDraft('')
    setSubmitting(false)
    textareaRef.current?.focus()
  }

  function handleKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault()
      void handleSubmit()
    }
  }

  return (
    <div className="space-y-3">
      {/* Composer */}
      <div className="space-y-1.5">
        <Textarea
          ref={textareaRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          rows={2}
          className="resize-none text-sm"
          aria-label="Note body"
        />
        <div className="flex items-center justify-end">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => void handleSubmit()}
            disabled={!draft.trim() || submitting}
            className="h-7 text-xs"
          >
            Add note
          </Button>
        </div>
      </div>

      {/* Thread */}
      {notes && notes.length > 0 && (
        <ol className="space-y-2 pt-1">
          {notes.map((note) => (
            <NoteItem key={note.id} note={note} />
          ))}
        </ol>
      )}

      {notes !== undefined && notes.length === 0 && (
        <p className="text-xs text-muted-foreground italic">No notes yet.</p>
      )}
    </div>
  )
}

function NoteItem({ note }: { note: Note }) {
  const [confirmOpen, setConfirmOpen] = useState(false)
  const deleteNote = useDeleteNote()

  return (
    <>
      <li className="group relative rounded-md border border-border bg-muted/30 px-3 py-2 text-sm">
        <p className="whitespace-pre-wrap break-words leading-relaxed">{note.body}</p>
        <div className="flex items-center justify-between mt-1.5">
          <time
            dateTime={note.createdAt}
            className="text-[10px] text-muted-foreground tabular-nums"
          >
            {format(new Date(note.createdAt), 'MMM d, yyyy · h:mm a')}
          </time>
          <button
            type="button"
            onClick={() => setConfirmOpen(true)}
            className="max-md:opacity-100 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 text-muted-foreground hover:text-destructive focus-visible:text-destructive transition-colors focus-visible:outline-none max-md:p-2 max-md:-m-2"
            aria-label="Delete note"
          >
            <Trash2 size={11} />
          </button>
        </div>
      </li>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Delete this note?"
        description="This note will be permanently deleted and cannot be recovered."
        confirmLabel="Delete note"
        destructive
        onConfirm={() => deleteNote.mutate(note.id)}
      />
    </>
  )
}
